import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { API } from "@/lib/api";
import { PRICING } from "@/data/pricing";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";

export function PricingPreview() {
  const [plans, setPlans] = useState(PRICING?.plans || []);
  useEffect(() => {
    API.get("/pricing").then((r) => {
      if (r?.data?.plans?.length) setPlans(r.data.plans);
    }).catch(() => {});
  }, []);

  return (
    <section className="py-16 sm:py-24 lg:py-32 bg-rw-bg2 border-y border-rw-border" data-testid="pricing-preview-section">
      <div className="container-rw">
        <SectionHead
          eyebrow="Transparent Pricing"
          title="SEO plans with a clear scope."
          description="No vague retainers. Every plan names its deliverables — and SEO never promises a #1 ranking, so neither do we."
          align="center"
          testid="pricing-preview-head"
        />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5 lg:gap-4">
          {plans.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.07}>
              <div
                className={`relative flex h-full flex-col rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 ${
                  p.featured ? "border-rw-green/60 bg-rw-card glow-green" : "border-rw-border bg-rw-card hover:border-rw-border2"
                }`}
                data-testid={`pricing-preview-${p.id}`}
              >
                {p.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-rw-green px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-rw-bg">
                    Popular
                  </span>
                )}
                <h3 className="font-jakarta text-sm font-bold uppercase tracking-wider text-rw-text">{p.name}</h3>
                <p className="mt-3 font-jakarta text-2xl font-extrabold text-rw-green">
                  {p.price}<span className="text-sm font-medium text-rw-dim">{p.period}</span>
                </p>
                <p className="mt-2 text-xs text-rw-muted leading-relaxed">{p.tagline}</p>
                <ul className="mt-4 space-y-2 flex-1">
                  {p.features.slice(0, 3).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-rw-muted">
                      <Check size={13} className="mt-0.5 shrink-0 text-rw-green" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 text-center">
          <Link to="/pricing" data-testid="pricing-preview-compare"
            className="inline-flex items-center gap-2 rounded-full border border-rw-green/60 px-8 py-3.5 text-sm font-semibold text-rw-green transition-all hover:bg-rw-green hover:text-rw-bg">
            Compare SEO Plans <ArrowUpRight size={15} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
